import * as Sentry from "@sentry/react";
import decode from "jwt-decode";
import { noop } from "lodash";
import { compose } from "redux";
import { from, Observable, of } from "rxjs";
import { SuperAgentRequest } from "superagent";
import { store } from "..";
import { API } from "../../services/http";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { generateDownload } from "../common/utils";
import {
  processErrorFromPromise,
  processErrorFromPromiseForAuth,
} from "../errors/utils";
import { NotificationActions } from "../notifications/actions";
import { successNotificationFromPromise } from "../notifications/utils";
import { UserActions } from "../users/actions";
import { AuthActions } from "./actions";
import { AuthDTO } from "./models/dto";

export namespace AuthService {
  const api = new API("auth");

  export function setLogin(token: AuthDTO.Responses.Login.T) {
    Sentry.setUser({
      "sek-user":
        token.accessToken.originalUser?.type === "DELEGATE"
          ? token.accessToken.originalUser.id
          : token.accessToken.credentialsId,
      email: token.accessToken.username,
    });
  }

  export function clearLogin(): Observable<boolean> {
    Sentry.configureScope((scope) => scope.setUser(null));
    return of(true);
  }

  const loginResultCall = (r: SuperAgentRequest) =>
    r
      .then<FetchableSuccess<AuthDTO.Responses.Login.T>>((response) => ({
        status: "ready",
        data: {
          accessToken: {
            ...decode(response.body.accessToken),
          },
          raw: response.body.accessToken,
          twoFactorAuth: response.body.twoFactorAuth,
        } as AuthDTO.Responses.Login.T, // TODO: Validate response
      }))
      .catch<FetchableError>(processErrorFromPromiseForAuth)
      .catch<FetchableError>((err) => ({
        status: "error",
        err,
      }));

  export function postLogin(
    data: AuthDTO.Requests.AuthRequest.T
  ): Promise<FetchableError | FetchableSuccess<AuthDTO.Responses.Login.T>> {
    return loginResultCall(api.post("v1/public/users/login").send(data));
  }

  export function impersonate(
    data: AuthDTO.Requests.Impersonate.T
  ): Promise<FetchableError | FetchableSuccess<AuthDTO.Responses.Login.T>> {
    return loginResultCall(api.post("v1/users/impersonate").send(data));
  }

  export function stopImpersonation(): Promise<
    FetchableError | FetchableSuccess<AuthDTO.Responses.Login.T>
  > {
    return loginResultCall(api.delete("v1/users/impersonate"));
  }

  export function resetPassword(
    id: string,
    passwordResetRequest: AuthDTO.Requests.ResetPassword.T
  ): Promise<unknown> {
    return api
      .put(`v1/users/${id}/password`)
      .send(passwordResetRequest)
      .then(
        successNotificationFromPromise("password-has-been-succefully-reset")
      )
      .then(noop)
      .catch(processErrorFromPromise);
  }

  export function unauthedResetPassword(
    token: string,
    passwordResetRequest: AuthDTO.Requests.UnauthedResetPassword.T
  ): Promise<unknown> {
    return api
      .post(`v1/public/users/forgot-password/confirmation`)
      .query({
        token,
      })
      .send(passwordResetRequest)
      .then(
        successNotificationFromPromise("password-has-been-succefully-reset")
      )
      .then(() => {})
      .catch(processErrorFromPromise);
  }

  export function forgotPassword(
    data: AuthDTO.Requests.ForgotPassword.T
  ): Promise<string> {
    return api
      .post("v1/public/users/forgot-password")
      .send(data)
      .then((response) => response.body)
      .then(
        successNotificationFromPromise("email-sent-please-check-your-inbox")
      )
      .catch(processErrorFromPromise);
  }

  export function logout(): Promise<void> {
    return api
      .post("v1/users/logout")
      .then((response) => response.body)
      .then(successNotificationFromPromise("you-have-logged-out"))
      .catch(processErrorFromPromise);
  }

  export function authenticated(): Promise<
    FetchableError | FetchableSuccess<AuthDTO.Responses.Login.T>
  > {
    return loginResultCall(api.get("v1/users/authenticated"));
  }

  export function individualSignUp(
    signUpDetails: AuthDTO.Requests.CreateIndividualUser.T
  ): Promise<AuthDTO.Responses.UserCreated> {
    return api
      .post("v1/public/users/signup")
      .send(signUpDetails)
      .then((response) => response.body)
      .then(
        successNotificationFromPromise(
          "your-account-has-been-created-please-check-your-inbox"
        )
      )
      .catch(processErrorFromPromise);
  }

  export function merchantSignUp(
    signUpDetails: AuthDTO.Requests.CreateMerchantUser.T
  ): Promise<AuthDTO.Responses.UserCreated> {
    return api
      .post("v1/public/merchants")
      .send(signUpDetails)
      .then((response) => response.body)
      .then(
        successNotificationFromPromise(
          "your-account-has-been-created-please-check-your-inbox"
        )
      )
      .catch(processErrorFromPromise);
  }

  export function adminSignUp(
    signUpDetails: AuthDTO.Requests.CreateIndividualUser.T
  ): Promise<AuthDTO.Responses.UserCreated> {
    return api
      .post("v1/users/signup")
      .send(signUpDetails)
      .then((response) => response.body)
      .then(
        successNotificationFromPromise(
          "your-account-has-been-created-please-check-your-inbox"
        )
      )
      .catch(processErrorFromPromise);
  }

  export function verifyEmail(token: string): Promise<void> {
    return api
      .get("v1/public/users/verify")
      .query({ token })
      .then((response) => response.body)
      .catch(processErrorFromPromise);
  }

  export function postTwoFactorRequest(
    data: AuthDTO.Requests.TwoFactorRequest.T
  ): Promise<AuthDTO.Responses.Login.T> {
    return api
      .post("v1/users/login/2fa")
      .send(data)
      .then<AuthDTO.Responses.Login.T, AuthDTO.Responses.Login.T>(
        (response) => ({
          accessToken: {
            ...decode(response.body.accessToken),
            authenticated: true,
          },
          raw: response.body.accessToken,
          twoFactorAuth: response.body.twoFactorAuth,
        })
      )
      .catch((err: any) => {
        if (err.status === 401) {
          store.dispatch(
            NotificationActions.setNotification({
              title: "otp-is-incorrect",
              variant: "error",
            })
          );
        }
        return Promise.reject(err);
      });
  }

  export const downloadMarketingEmail = (excludeSanctionedUsers: boolean) =>
    generateDownload(api, "admin/users/marketing/emails", {
      headers: {
        Accept: "text/csv",
      },
      query: {
        excludeSanctionedUsers,
      },
      verb: "post",
    })();

  export const updateMarketingSettings = (
    data: AuthDTO.Requests.UpdateMarketingSettings.T
  ): Promise<void> => {
    const login = store.getState().auth.login;
    if (login.status !== "ready") throw new Error("User not logged in");

    return api
      .put(`v1/users/${login.data.accessToken.credentialsId}/marketing`)
      .send(data)
      .then<void>(() => {
        store.dispatch(UserActions.resetUserDetails());
      })
      .then<void>(
        successNotificationFromPromise("settings-successfully-updated")
      )
      .catch<void>(processErrorFromPromise);
  };

  export const setUserLocale = (locale: string): Promise<void> => {
    const login = store.getState().auth.login;
    if (login.status !== "ready") throw new Error("User not logged in");

    return api
      .put(`v1/users/${login.data.accessToken.credentialsId}/locale`)
      .send({ locale })
      .then<void>(() => {
        store.dispatch(UserActions.resetUserDetails());
      })
      .then<void>(
        successNotificationFromPromise("settings-successfully-updated")
      )
      .catch<void>(processErrorFromPromise);
  };

  export const updateCryptoSettings = (enabled: boolean): Promise<void> => {
    const login = store.getState().auth.login;
    if (login.status !== "ready") throw new Error("User not logged in");

    return api
      .put(`v1/users/${login.data.accessToken.credentialsId}/crypto`)
      .send({ enabled })
      .then<void>(() => {
        store.dispatch(AuthActions.logout());
      })
      .catch<void>(processErrorFromPromise);
  };
}
